import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Info as InfoCircleIcon } from 'lucide-react'
import { formatApplicantIdSegment } from '../../utils/applicantIdFormat'
import {
  reimbursementRentalProgressLabel,
  reimbursementStatusLabel,
  reimbursementTypeLabel,
  rentPaidStatusBadgeClass,
  rentPaidStatusLabel,
  isActiveResidentRentalStatus,
  residentRentalProgressLabel,
  residentRentalStatusLabel,
  residentRentalStatusTextClass,
} from '../../utils/residentListStatusStyles'
import { AccessConfirmationIconButton } from '@/components/ui/AccessConfirmationIconButton'
import { TableSearchFilterEmptyPanel } from '@/components/ui/TableSearchFilterEmptyState'
import { formatResidentListDate } from '../../utils/residentDateUtils'
import type { ResidentGridProps, ResidentListRow } from '../../types'

/** Angular grid Select-All (`.html:297-301`) has no matching prop in the shared type. */
type ResidentGridPropsWithSelectAll = ResidentGridProps & {
  onSelectAll?: (checked: boolean) => void
}

/**
 * Angular `canShowAccessConfirmationButton` (resident-list.component.ts:940-949):
 * show only when balance_due is null/empty/NaN or <= 0.
 */
function canShowAccessConfirmation(balanceDue?: number | string | null): boolean {
  if (balanceDue === undefined || balanceDue === null || balanceDue === '') return true
  const n = Number(balanceDue)
  if (Number.isNaN(n)) return true
  return n <= 0
}

function formatDate(value?: string, yearOnly = false) {
  return formatResidentListDate(value, yearOnly ? 'year' : 'date')
}

export function ResidentGrid({
  rows,
  statusLabelMaps,
  selectedApplicantIds,
  canCompanyView,
  canPropertyView,
  canSelect,
  canDetailView,
  busyAccessConfirmationApplicationId,
  onSelectRow,
  onSelectAll,
  onAccessConfirmation,
}: ResidentGridPropsWithSelectAll) {
  // Angular `selectAllUser` selects only Active residents.
  const selectableActive = rows.filter((resident) =>
    isActiveResidentRentalStatus(resident.resident_rental_status) && resident.reimbursement_type === null,
  )
  const allSelected =
    selectableActive.length > 0 &&
    selectableActive.every((resident) =>
      selectedApplicantIds.includes(resident.applicant_id),
    )

  if (rows.length === 0)
    return (
      <TableSearchFilterEmptyPanel
        className="m-3 border-0 shadow-none sm:m-4"
        data-testid="resident-grid-empty"
      />
    )

  return (
    <div
      className="grid min-w-0 grid-cols-1 gap-3 p-3 sm:gap-4 sm:p-4 md:grid-cols-2 xl:grid-cols-3"
      data-testid="resident-grid"
    >
      {/* Angular grid Select-All checkbox (`.html:297-301`). */}
      {canSelect ? (
        <div className="col-span-full" data-testid="resident-grid-select-all-cell">
          <label className="inline-flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(event) => onSelectAll?.(event.target.checked)}
              data-testid="resident-grid-select-all"
              aria-label="Select all residents"
              className="h-4 w-4 shrink-0"
            />
            Select All
          </label>
        </div>
      ) : null}
      {rows.map((resident) => {

        const idSeg = formatApplicantIdSegment(resident.applicant_id)
        const showCheckBox = (isActiveResidentRentalStatus(resident.resident_rental_status) && resident.reimbursement_type === null)

        const isBusy =
          busyAccessConfirmationApplicationId != null &&
          String(busyAccessConfirmationApplicationId) ===
            String(resident.application_id)
        return (
          <article
            key={`${resident.applicant_id}-${resident.fk_user_id}`}
            className="min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            data-testid={`resident-grid-card-${idSeg}`}
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs uppercase text-slate-500">Applicant</p>
                {canDetailView ? (
                  <Link
                    className="block truncate text-base font-semibold text-blue-600 underline"
                    to={`/resident/resident-detail/${idSeg}/${resident.fk_user_id}`}
                    data-testid={`resident-grid-card-${idSeg}-applicant-link`}
                  >
                    {idSeg}
                  </Link>
                ) : (
                  <p className="truncate font-semibold">{idSeg}</p>
                )}
              </div>
              {canSelect && showCheckBox? (
                <input
                  type="checkbox"
                  checked={selectedApplicantIds.includes(resident.applicant_id)}
                  onChange={(event) =>
                    onSelectRow(resident, event.target.checked)
                  }
                  data-testid={`resident-grid-card-${idSeg}-select`}
                  aria-label={`Select resident ${idSeg}`}
                  className="h-4 w-4 shrink-0"
                />
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <Info
                testId={`resident-grid-card-${idSeg}-name`}
                label="Resident Name"
                value={`${resident.first_name || ''} ${resident.last_name || ''}`.trim()}
              />
              <Info
                testId={`resident-grid-card-${idSeg}-unit`}
                label="Unit #"
                value={resident.unit_number}
              />
              {canCompanyView ? (
                <Info
                  testId={`resident-grid-card-${idSeg}-company`}
                  label="Company"
                  value={resident.company_name}
                />
              ) : null}
              {canPropertyView ? (
                <Info
                  testId={`resident-grid-card-${idSeg}-property`}
                  label="Property"
                  value={resident.property_name}
                />
              ) : null}
              <Info
                testId={`resident-grid-card-${idSeg}-lease-start`}
                label="Contract Start Date"
                value={formatDate(resident.contract_start_date)}
              />
              <Info
                testId={`resident-grid-card-${idSeg}-vintage-year`}
                label="Vintage Year"
                value={formatDate(resident.contract_start_date, true)}
              />
              <RentalStatusCell
                resident={resident}
                statusLabelMaps={statusLabelMaps}
                testId={`resident-grid-card-${idSeg}-resident-rental-status`}
              />
              <Info
                testId={`resident-grid-card-${idSeg}-resident-rental-progress`}
                label="Resident Progress"
                value={
                  residentRentalProgressLabel(resident.resident_rental_progress, statusLabelMaps) ||
                  resident.resident_rental_progress
                }
              />
              <Info
                testId={`resident-grid-card-${idSeg}-reimbursement-type`}
                label="Reimbursement Type"
                value={reimbursementTypeLabel(resident.reimbursement_type, statusLabelMaps)}
              />
              <Info
                testId={`resident-grid-card-${idSeg}-reimbursement-rental-status`}
                label="Reimbursement Status"
                value={reimbursementStatusLabel(resident.reimbursement_rental_status, statusLabelMaps)}
              />
              <Info
                testId={`resident-grid-card-${idSeg}-reimbursement-rental-progress`}
                label="Reimbursement Progress"
                value={
                  reimbursementRentalProgressLabel(
                    resident.reimbursement_rental_progress,
                    statusLabelMaps,
                  ) ||
                  resident.reimbursement_rental_progress
                }
              />
              <div
                className="min-w-0"
                data-testid={`resident-grid-card-${idSeg}-pay-status`}
              >
                <p className="text-xs uppercase text-slate-500">Rent Paid Status</p>
                <span className={rentPaidStatusBadgeClass(resident.pay_status)}>
                  {rentPaidStatusLabel(resident.pay_status, statusLabelMaps)}
                </span>
              </div>
            </div>
            {resident.application_id &&
            canShowAccessConfirmation(resident.balance_due) ? (
              <div className="mt-4 flex items-center justify-end">
                <AccessConfirmationIconButton
                  onClick={() =>
                    onAccessConfirmation(
                      resident.application_id as string | number,
                    )
                  }
                  busy={isBusy}
                  data-testid={`resident-grid-card-${idSeg}-access-confirmation-button`}
                  className="rounded-full p-2"
                />
              </div>
            ) : null}
          </article>
        )
      })}
    </div>
  )
}

/**
 * Angular grid Rental Status cell (`.html:344-355`): value plus a red info icon that,
 * when `rental_status_required` has entries, toggles a "Required*" list.
 */
function RentalStatusCell({
  resident,
  statusLabelMaps,
  testId,
}: {
  resident: ResidentListRow
  statusLabelMaps?: ResidentGridProps['statusLabelMaps']
  testId?: string
}) {
  const [open, setOpen] = useState(false)
  const required = resident.rental_status_required ?? []
  return (
    <div className="min-w-0" data-testid={testId}>
      <p className="text-xs uppercase text-slate-500">Resident Status</p>
      <p
        className={`break-words ${residentRentalStatusTextClass(resident.resident_rental_status)}`}
      >
        {residentRentalStatusLabel(resident.resident_rental_status, statusLabelMaps)}
        {required.length ? (
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="ml-1 inline-flex align-middle text-red-600"
            aria-label="Required rental status values"
            data-testid={`${testId}-required-icon`}
          >
            <InfoCircleIcon className="h-4 w-4" />
          </button>
        ) : null}
      </p>
      {open && required.length ? (
        <div
          className="mt-1 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-slate-800"
          data-testid={`${testId}-required-list`}
        >
          <p className="font-semibold">Required*</p>
          {required.map((status) => (
            <p
              key={status}
              className="cursor-pointer"
              onClick={() => setOpen(false)}
            >
              {status}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function Info({
  label,
  value,
  testId,
}: {
  label: string
  value?: string
  testId?: string
}) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <p className="text-xs uppercase text-slate-500">{label}</p>
      <p className="break-words font-medium text-slate-800">{value || '-'}</p>
    </div>
  )
}
